import { useEffect, useState } from 'react'
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { editDetailedSummaryModalSchema } from './form/validations'
import { EditIcon } from '@assets/svgs/EditIcon'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowBox } from '@components/shared/RowBox'
import { setInitialValues } from './helpers/setInitialValues'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { useInvoicing } from '@pages/Invoicing/context'
import { useUser } from '@hooks/useUser'
import { WarningIcon } from '@assets/svgs/WarningIcon'

import type { DetailedSummaryModalProps } from './types'
import type { EditDetailedSummaryFormProps } from './form/types'

import * as S from './styles'

export const EditDetailedSummaryModal = ({ data, onClose, canEdit = true, ...rest }: DetailedSummaryModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData } = useUser()
  const { isEditDetailedSummaryLoading, editDetailedSummary } = useInvoicing()

  const methods = useForm<EditDetailedSummaryFormProps>({
    defaultValues: initialFormValues,
    resolver: editDetailedSummaryModalSchema as unknown as Resolver<EditDetailedSummaryFormProps>
  })

  const { handleSubmit, setValue, reset, trigger } = methods

  const [openCancelDetailedSummaryModal, setOpenCancelDetailedSummaryModal] = useState(false)

  const onError = (errors: FieldErrors<EditDetailedSummaryFormProps>) => console.log({ errors })

  const onSubmit = (formData: EditDetailedSummaryFormProps) => {
    if (!canEdit) return

    formData.id = data?.detailedBillingId || null
    formData.filiCode = filiData?.filiCode || ''
    formData.competenceDate = filiData?.startPeriod || ''

    editDetailedSummary(formData).then((success) => success && onClose(false))
  }

  const handleSetInitialValues = () => reset(setInitialValues(data!))

  useEffect(() => {
    !!data && handleSetInitialValues()
  }, [data])

  return (
    <>
      <BaseModal2
        title="Editar Resumo Detalhado"
        headerIcon={<EditIcon />}
        onClose={() => setOpenCancelDetailedSummaryModal(true)}
        width={isMobile ? 'calc(100% - 20px)' : 430}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : '100%',
          confirmButtonText: canEdit ? 'Confirmar' : undefined,
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : '100%',
          loading: isEditDetailedSummaryLoading,
          onConfirm: canEdit ? () => handleSubmit(onSubmit, onError)() : undefined
        }}
        {...rest}
      >
        <S.Title>Informe aqui os valores do Resumo detalhado.</S.Title>

        <S.Warning>
          <WarningIcon />
          Inserir o valor líquido
        </S.Warning>

        <FormProvider {...methods}>
          <RowBox>
            <Input
              name="minimumBilling"
              label="Faturamento Mínimo"
              maskType="currency"
              placeholder="R$ 0,00"
              onBlur={({ target }) => {
                setValue('minimumBilling', unMaskCurrency(target.value).toFixed(2))
              }}
              disabled={!canEdit}
            />

            <Input
              name="provisions"
              label="Provisões (+)"
              maskType="currency"
              placeholder="R$ 0,00"
              onBlur={({ target }) => {
                setValue('provisions', unMaskCurrency(target.value).toFixed(2))
                trigger('provisions')
              }}
              disabled={!canEdit}
            />

            <Input
              name="reversals"
              label="Reversões (-)"
              maskType="currency"
              placeholder="R$ 0,00"
              forceNegative
              onBlur={({ target }) => {
                setValue('reversals', (-Math.abs(unMaskCurrency(target.value))).toFixed(2))
                trigger('reversals')
              }}
              disabled={!canEdit}
            />

            <Input
              name="adjustments"
              label="Reajustes"
              maskType="currency"
              placeholder="R$ 0,00"
              onBlur={({ target }) => setValue('adjustments', unMaskCurrency(target.value).toFixed(2))}
              disabled={!canEdit}
            />
          </RowBox>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelDetailedSummaryModal}
        headerIcon={<WarningIcon />}
        headerTitle="Alterações não salvas"
        message="Ao confirmar, as alterações realizadas não serão salvas."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelDetailedSummaryModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelDetailedSummaryModal(false)
        }}
      />
    </>
  )
}
